import { useEffect, useState, useCallback, useRef } from "react";
import { PageTitle } from "@/components/PageHeader";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useCachedState } from "@/lib/swr";
import { useDash } from "./ctx";
import { V, useVocab } from "@/lib/vocab";
import { Link } from "react-router-dom";
import ConversationPanel from "./ConversationPanel";
import { usePaged, ShowMore } from "@/components/Paged";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Sheet, SheetTrigger, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { MessagesSquare, Phone, MessageSquare, ArrowLeft, Search, X, AlertTriangle, SlidersHorizontal, ShieldCheck, Mail, ChevronDown, History } from "lucide-react";

const STATUSES = [["", "All"], ["active", "Active"], ["escalated", "Escalated"], ["resolved", "Resolved"], ["archived", "Archived"], ["notices", "Notices only"]];
const CHANNELS = [["", "All channels"], ["text", "Text"], ["voice", "Voice"]];
const DATES = [["", "Any time"], ["1", "Last 24 hours"], ["7", "Last 7 days"], ["30", "Last 30 days"]];
const noScrollbar = "[scrollbar-width:none] [&::-webkit-scrollbar]:hidden";

const STATUS_STYLE = {
  active: "bg-blue-50 text-blue-700",
  escalated: "bg-amber-50 text-amber-700",
  waiting: "bg-sky-50 text-sky-700",
  human_handling: "bg-redline-50 text-redline-700",
  resolved: "bg-emerald-50 text-emerald-700",
};
const ATTENTION = new Set(["escalated", "human_handling", "waiting"]);
const DATE_DAYS = { "": Infinity, "1": 1, "7": 7, "30": 30 };

const fmtTime = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return ""; }
};
const relTime = (iso) => {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  if (isNaN(diff)) return "";
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return fmtTime(iso);
};
const fmtPhone = (p) => {
  if (!p) return "";
  const d = String(p).replace(/[^\d]/g, "").replace(/^1/, "");
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : p;
};

function ConversationsEmpty({ tenant, v }) {
  const people = v.lcs("person");
  const number = tenant?.phone_number ? fmtPhone(tenant.phone_number) : null;
  const steps = [
    { n: 1, title: number ? `Share ${number}` : "Get a number", body: number ? `Put it on the ${v.isHoa ? "welcome packet, gate and website" : "invoice, truck and website"} — or invite ${people} from the ${v.mod("roster")} page.` : "Ask Redline to assign this concierge a phone number first." },
    { n: 2, title: `${v.many("person")} text or call it`, body: "The concierge answers instantly from your documents, facts and policies — day or night." },
    { n: 3, title: "Threads land here", body: "You read along, step in when a thread needs a human, and mark it resolved." },
  ];
  return (
    <div className="bg-white rounded-2xl border border-sand-200 p-6 sm:p-8" data-testid="conv-empty">
      <div className="flex items-start gap-4">
        <div className="h-12 w-12 rounded-2xl bg-redline-50 flex items-center justify-center shrink-0"><MessagesSquare className="h-6 w-6 text-redline-500" /></div>
        <div className="min-w-0">
          <div className="font-heading font-bold text-ink text-lg">No conversations yet — that's expected</div>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">You don't start conversations here. {v.many("person")} do, by texting or calling{number ? <> <span className="font-semibold text-ink whitespace-nowrap">{number}</span></> : " the concierge number"}. Every one of them shows up on this screen as it happens.</p>
        </div>
      </div>
      <ol className="mt-6 grid gap-3 sm:grid-cols-3">
        {steps.map((st) => (
          <li key={st.n} className="rounded-xl border border-sand-200 p-4" data-testid={`conv-empty-step-${st.n}`}>
            <div className="h-6 w-6 rounded-full bg-ink text-white text-xs font-bold flex items-center justify-center">{st.n}</div>
            <div className="font-semibold text-sm mt-2">{st.title}</div>
            <div className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{st.body}</div>
          </li>
        ))}
      </ol>
      <div className="mt-6 flex flex-wrap gap-2 text-sm">
        <Link to="/dashboard/simulator" data-testid="conv-empty-simulator" className="rounded-full bg-ink text-white px-4 py-2 font-semibold hover:bg-ink-700 transition-colors">Try it yourself in Live Demo</Link>
        <Link to="/dashboard/roster" data-testid="conv-empty-invite" className="rounded-full border border-sand-200 bg-white px-4 py-2 font-semibold hover:border-ink transition-colors">Invite {people}</Link>
        {number && <span className="rounded-full border border-dashed border-sand-300 px-4 py-2 text-ink/70">Or text “hi” to {number} from your own phone</span>}
      </div>
    </div>
  );
}

// One row in the list. Used for a caller's current thread and, indented, for each earlier one.
function ConvRow({ c, active, onOpen, nested }) {
  const isVoice = c.channel === "voice";
  const isEmailOnly = c.channel === "email" || (c.phone || "").startsWith("portal:");
  const attn = ATTENTION.has(c.status);
  return (
    <button data-testid={`conv-row-${c.id}`} onClick={() => onOpen(c.id)}
      className={`w-full flex items-start gap-3 text-left transition-colors border-l-[3px] ${
        nested ? "py-3 pl-10 pr-4 bg-sand-50/40 border-t border-sand-100" : "p-4"} ${
        active ? "bg-redline-50 border-l-redline-500"
        : attn ? "border-l-amber-400 hover:bg-sand-50"
        : "border-l-transparent hover:bg-sand-50"}`}>
      <div className={`rounded-full flex items-center justify-center font-bold shrink-0 ${
        nested ? "h-7 w-7 text-xs" : "h-10 w-10"} ${isVoice ? "bg-violet-50 text-violet-700" : "bg-redline-50 text-redline-600"}`}>
        {((c.active_person_name || c.staff_name || "?").trim()[0] || "?").toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold truncate max-w-[10rem]">{c.active_person_name || c.staff_name || "Unknown"}</span>
          {c.sender_kind === "staff" && (
            <span data-testid={`conv-staff-badge-${c.id}`} title={`${c.staff_name || "A teammate"} — ${c.staff_role === "board_member" ? `${V.lc("board")}` : `${V.lc("org")} manager`}`}
              className="text-[10px] px-1.5 py-0.5 rounded-full shrink-0 inline-flex items-center gap-1 bg-ink text-white">
              <ShieldCheck className="h-2.5 w-2.5" /> Staff
            </span>
          )}
          <span data-testid={`conv-channel-badge-${c.id}`} className={`text-[10px] px-1.5 py-0.5 rounded-full shrink-0 inline-flex items-center gap-1 ${isVoice ? "bg-violet-50 text-violet-700" : "bg-sand-100 text-ink/60"}`}>
            {isVoice ? <><Phone className="h-2.5 w-2.5" /> Voice</> : isEmailOnly ? <><Mail className="h-2.5 w-2.5" /> Email</> : <><MessageSquare className="h-2.5 w-2.5" /> Text</>}
          </span>
          <span className={`text-[10px] px-2 py-0.5 rounded-full capitalize shrink-0 ${STATUS_STYLE[c.status] || "bg-sand-100 text-muted-foreground"}`}>{(c.status || "").replace(/_/g, " ")}</span>
        </div>
        {c.phone && !nested && <div className="text-[11px] font-mono text-ink/45 mt-0.5">{c.phone.startsWith("portal:") ? "No mobile · email notices" : fmtPhone(c.phone)}</div>}
        <div className="text-sm text-muted-foreground truncate mt-0.5">{c.last_message || <span className="italic text-ink/30">{c.notices_only ? "Notices only — no conversation yet" : "No messages"}</span>}</div>
        <div className="flex items-center gap-2 mt-1 text-[10px] text-muted-foreground">
          <span title={fmtTime(c.last_message_at || c.created_at)}>{relTime(c.last_message_at || c.created_at)}</span>
          {c.message_count > 0 && <><span className="text-sand-300">·</span><span>{c.message_count} msg{c.message_count === 1 ? "" : "s"}</span></>}
        </div>
      </div>
    </button>
  );
}

function useIsLg() {
  const [isLg, setIsLg] = useState(() => typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const on = () => setIsLg(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return isLg;
}

export default function Conversations() {
  const { tenantId, role, tenant, tenants, allTenants, setTenantId } = useDash();
  const v = useVocab();
  const [params] = useSearchParams();
  const [filter, setFilter] = useState(params.get("status") || "");
  const [channel, setChannel] = useState(params.get("channel") === "voice" ? "voice" : "");
  const [dateRange, setDateRange] = useState("");
  const [query, setQuery] = useState("");
  const [items, setItems] = useCachedState(`convs:${tenantId}:${filter}`, []);
  const [openId, setOpenId] = useState(params.get("open") || null);
  // A deep link (bell, emergency alert, "Open record") lands straight on the thread on a phone too.
  const [mobileDetail, setMobileDetail] = useState(!!params.get("open"));
  const [openHistory, setOpenHistory] = useState({});
  // The deep-linked thread stays open even when it isn't in this community's list (older than the
  // list cap, or another community — see below); only a thread we listed and lost gets deselected.
  const deepLink = useRef(params.get("open") || null);
  useEffect(() => {
    const id = deepLink.current;
    if (!id || !tenantId) return;
    api.get(`/conversations/${id}`).then((r) => {
      const tid = r.data?.conversation?.tenant_id;
      if (!tid || tid === tenantId) return;
      // iter479d — an alert link opened in the wrong community showed a blank panel. Follow the
      // thread to its own community when this person can work there.
      const home = (allTenants || tenants || []).find((t) => t.id === tid);
      if (home) {
        setTenantId(tid);
        toast.message(`Switched to ${home.name || "the conversation's community"}`);
      } else {
        deepLink.current = null; setOpenId(null);
        toast.error("That conversation belongs to a community you don't have access to.");
      }
    }).catch((e) => {
      const st = e?.response?.status;
      if (st === 404 || st === 403) {
        deepLink.current = null; setOpenId(null);
        toast.error(st === 404 ? "That conversation no longer exists." : "That conversation belongs to a community you don't have access to.");
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [expanded, setExpanded] = useState(false);
  const isLg = useIsLg();
  const [listW, setListW] = useState(() => {
    const v = parseInt(typeof window !== "undefined" ? (localStorage.getItem("askredline.convListW") || "") : "", 10);
    return Number.isFinite(v) && v >= 260 && v <= 640 ? v : 360;
  });
  const draggingRef = useRef(false);
  const startResize = (e) => {
    e.preventDefault();
    draggingRef.current = true;
    const startX = e.clientX;
    const startW = listW;
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
    const onMove = (ev) => {
      const w = Math.min(640, Math.max(260, startW + (ev.clientX - startX)));
      setListW(w);
    };
    const onUp = () => {
      draggingRef.current = false;
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
      setListW((w) => { try { localStorage.setItem("askredline.convListW", String(w)); } catch {} return w; });
    };
    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onUp);
  };
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [loaded, setLoaded] = useState(false);
  const load = useCallback(() => {
    if (!tenantId) return;
    const q = (filter && filter !== "attention") ? `?status=${filter}` : "";
    api.get(`/tenants/${tenantId}/conversations${q}`).then((r) => {
      setItems(r.data); setLoaded(true);
      // Keep an already-open thread if it still exists; otherwise leave it unselected
      // (no auto-pick of the first row — a pre-highlighted card reads like an error, esp. on mobile).
      setOpenId((prev) => (prev && (prev === deepLink.current || r.data.some((c) => c.id === prev))) ? prev : null);
    }).catch(() => { setLoaded(true); toast.error("Couldn't refresh conversations — showing the last loaded list."); });
  }, [tenantId, filter]);

  useEffect(() => { load(); }, [load]);

  const q = query.trim().toLowerCase();
  const shown = items.filter((c) => {
    if (filter === "attention" && !ATTENTION.has(c.status)) return false;
    if (channel === "text" && c.channel === "voice") return false;
    if (channel === "voice" && c.channel !== "voice") return false;
    if (dateRange) {
      const t = new Date(c.last_message_at || c.created_at || 0).getTime();
      if (isNaN(t) || Date.now() - t > DATE_DAYS[dateRange] * 86400000) return false;
    }
    if (q && !`${c.active_person_name || ""} ${c.staff_name || ""} ${c.phone || ""} ${c.last_message || ""}`.toLowerCase().includes(q)) return false;
    return true;
  });
  const attentionCount = items.filter((c) => ATTENTION.has(c.status)).length;
  const activeFilterCount = (channel ? 1 : 0) + (dateRange ? 1 : 0);
  // One row per CALLER, not per thread. A thread closes after a quiet spell and the next text
  // starts a fresh one, so a regular caller legitimately has several records — which read as
  // duplicates in a flat list. `shown` is already newest-first, so the first record for a number
  // is its current thread and the rest are its history.
  const groups = [];
  const groupIndex = new Map();
  for (const c of shown) {
    const key = c.phone || c.staff_name || c.id;
    const at = groupIndex.get(key);
    if (at === undefined) {
      groupIndex.set(key, groups.length);
      groups.push({ key, head: c, earlier: [] });
    } else {
      groups[at].earlier.push(c);
    }
  }
  const { visible, remaining, showMore } = usePaged(groups, { step: 30, resetKey: `${filter}|${channel}|${dateRange}|${q}` });
  const openRow = (id) => { setOpenId(id); setMobileDetail(true); };
  const toggleHistory = (key) => setOpenHistory((s) => ({ ...s, [key]: !s[key] }));

  useEffect(() => {
    if (!expanded) return;
    const onKey = (e) => { if (e.key === "Escape") setExpanded(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [expanded]);
  useEffect(() => { if (!openId) setExpanded(false); }, [openId]);

  const pill = (active, tone = "ink") => `rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${
    active ? (tone === "redline" ? "bg-redline-500 text-white" : "bg-ink text-white")
           : "bg-white border border-sand-200 hover:border-ink text-ink"}`;

  const header = (
    <div>
      <PageTitle title="Conversation History" about="A searchable audit trail of every conversation your AI concierge has handled, with the sources behind each answer." />
    </div>
  );

  // First visit, nothing has happened yet: explain what this screen is FOR instead of showing an
  // empty inbox with filters (owner: "I'd think it was for me to have conversations").
  if (loaded && !filter && items.length === 0) {
    return (
      <div className="space-y-5">
        {header}
        <ConversationsEmpty tenant={tenant} v={v} />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {header}

      {/* Toolbar */}
      <div className="bg-white rounded-2xl border border-sand-200 p-3 sm:p-4 space-y-3">
        {/* Row 1: search + secondary filters (desktop selects / mobile sheet) */}
        <div className="flex items-center gap-2">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-sand-400" />
            <input data-testid="conv-search" value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder={`Search ${V.lc("person")}, number or message`}
              className="w-full rounded-full border border-sand-200 bg-sand-50 pl-10 pr-9 py-2.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30 focus:bg-white" />
            {query && <button data-testid="conv-search-clear" onClick={() => setQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-sand-400 hover:text-ink"><X className="h-4 w-4" /></button>}
          </div>

          {/* Desktop: channel + time as compact selects */}
          <div className="hidden sm:flex items-center gap-2 shrink-0">
            <Select value={channel || "all"} onValueChange={(v) => setChannel(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="conv-channel-select" className="w-[150px] rounded-full bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>{CHANNELS.map(([v, l]) => <SelectItem key={v || "all"} value={v || "all"} data-testid={`conv-channel-opt-${v || "all"}`}>{l}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={dateRange || "any"} onValueChange={(v) => setDateRange(v === "any" ? "" : v)}>
              <SelectTrigger data-testid="conv-date-select" className="w-[160px] rounded-full bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>{DATES.map(([v, l]) => <SelectItem key={v || "any"} value={v || "any"} data-testid={`conv-date-opt-${v || "any"}`}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          {/* Mobile: channel + time tucked behind a Filters sheet */}
          <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
            <SheetTrigger asChild>
              <button data-testid="conv-filters-trigger" aria-label="Filters" className="sm:hidden relative shrink-0 inline-flex items-center gap-1.5 rounded-full border border-sand-200 bg-white px-4 py-2.5 text-sm font-medium text-ink hover:border-ink transition-colors">
                <SlidersHorizontal className="h-4 w-4" /> Filters
                {activeFilterCount > 0 && <span className="absolute -top-1.5 -right-1.5 min-w-5 h-5 px-1 rounded-full bg-redline-500 text-white text-[10px] font-bold flex items-center justify-center">{activeFilterCount}</span>}
              </button>
            </SheetTrigger>
            <SheetContent side="bottom" className="rounded-t-2xl max-h-[85dvh] overflow-y-auto" data-testid="conv-filters-sheet">
              <SheetHeader className="flex-row items-center justify-between space-y-0 text-left">
                <SheetTitle>Filters</SheetTitle>
                {activeFilterCount > 0 && <button data-testid="conv-clear-filters" onClick={() => { setChannel(""); setDateRange(""); }} className="text-sm font-semibold text-redline-600 hover:text-redline-700">Clear all</button>}
              </SheetHeader>
              <div className="space-y-5 py-5">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wide text-ink/40 mb-2">Channel</div>
                  <div className="flex flex-wrap gap-2">
                    {CHANNELS.map(([v, l]) => (
                      <button key={v || "all"} data-testid={`conv-channel-${v || "all"}`} onClick={() => setChannel(v)} className={`${pill(channel === v, "redline")} inline-flex items-center gap-1.5`}>
                        {v === "voice" && <Phone className="h-3.5 w-3.5" />}{v === "text" && <MessageSquare className="h-3.5 w-3.5" />}{l}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wide text-ink/40 mb-2">Time range</div>
                  <div className="flex flex-wrap gap-2">
                    {DATES.map(([v, l]) => (
                      <button key={v || "any"} data-testid={`conv-date-${v || "any"}`} onClick={() => setDateRange(v)} className={pill(dateRange === v)}>{l}</button>
                    ))}
                  </div>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>

        {/* Row 2: triage + status (scrolls horizontally on mobile, no clutter) */}
        <div className={`flex items-center gap-2 overflow-x-auto ${noScrollbar} -mx-1 px-1`}>
          {attentionCount > 0 && (
            <>
              <button data-testid="conv-filter-attention" onClick={() => setFilter(filter === "attention" ? "" : "attention")}
                className={`shrink-0 rounded-full px-3.5 py-1.5 text-sm font-semibold inline-flex items-center gap-1.5 border transition-colors ${
                  filter === "attention" ? "bg-redline-500 text-white border-redline-500" : "bg-redline-50 text-redline-600 border-redline-200 hover:bg-redline-100"}`}>
                <AlertTriangle className="h-3.5 w-3.5" /> {attentionCount} need{attentionCount === 1 ? "s" : ""} attention
              </button>
              <span className="shrink-0 h-5 w-px bg-sand-200 mx-0.5" />
            </>
          )}
          {STATUSES.map(([v, l]) => (
            <button key={v || "all"} data-testid={`conv-filter-${v || "all"}`} onClick={() => setFilter(v)} className={`shrink-0 ${pill(filter === v)}`}>{l}</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 lg:gap-0 items-start"
        style={isLg && !expanded ? { gridTemplateColumns: `${listW}px 16px minmax(0,1fr)` } : undefined}>
        {/* List */}
        <div className={`min-w-0 ${mobileDetail ? "hidden lg:block" : "block"}`}>
          <div className="text-xs text-muted-foreground mb-2 px-1" data-testid="conv-result-count">
            {shown.length} conversation{shown.length === 1 ? "" : "s"}{(query || channel || dateRange) ? " matching" : ""}
            {groups.length !== shown.length && <> · {groups.length} caller{groups.length === 1 ? "" : "s"}</>}
          </div>
          <div className="bg-white rounded-2xl border border-sand-200 divide-y divide-sand-100 overflow-hidden max-h-[68vh] overflow-y-auto chat-scroll">
            {shown.length === 0 && items.length > 0 && (
              <div className="p-10 text-center text-muted-foreground flex flex-col items-center gap-2" data-testid="conv-empty-filtered">
                <MessagesSquare className="h-8 w-8 text-sand-300" /> No conversations match these filters.
              </div>
            )}
            {items.length === 0 && (
              <div className="px-8 py-12 text-center flex flex-col items-center gap-3" data-testid="conv-empty">
                <div className="h-12 w-12 rounded-2xl bg-sand-100 flex items-center justify-center"><MessagesSquare className="h-6 w-6 text-ink/40" /></div>
                <div className="font-heading font-bold text-ink text-base">Nothing here yet — and that&rsquo;s normal</div>
                <p className="text-sm text-muted-foreground max-w-md">
                  This is where every text and phone conversation the concierge has with your {v.lcs("person")} shows up, as it happens.
                  You don&rsquo;t start conversations here — {v.lcs("person")} do, by texting or calling
                  {tenant?.phone_number ? <> <span className="font-semibold text-ink whitespace-nowrap">{fmtPhone(tenant.phone_number)}</span></> : " the concierge number"}.
                  You read along, step in when a thread needs a human, and mark it resolved.
                </p>
                <div className="flex flex-wrap justify-center gap-2 mt-1 text-sm">
                  {tenant?.phone_number && <span className="rounded-full border border-sand-200 bg-white px-3 py-1.5 text-ink/70">Try it: text &ldquo;hi&rdquo; to {fmtPhone(tenant.phone_number)}</span>}
                  <Link to="/dashboard/simulator" data-testid="conv-empty-simulator" className="rounded-full bg-ink text-white px-3 py-1.5 font-semibold hover:bg-ink-700 transition-colors">Try a conversation in Live Demo</Link>
                </div>
              </div>
            )}
            {visible.map((g) => {
              const showHist = !!openHistory[g.key] || g.earlier.some((e) => e.id === openId);
              return (
                <div key={g.key} data-testid={`conv-group-${g.head.id}`}>
                  <ConvRow c={g.head} active={openId === g.head.id} onOpen={openRow} />
                  {g.earlier.length > 0 && (
                    <button type="button" data-testid={`conv-history-toggle-${g.head.id}`} onClick={() => toggleHistory(g.key)}
                      className="flex w-full items-center gap-1.5 border-t border-sand-100 bg-sand-50/60 py-1.5 pl-[4.25rem] pr-4 text-left text-[11px] font-semibold text-ink/50 transition-colors hover:text-ink/80">
                      <History className="h-3 w-3 shrink-0" />
                      {showHist ? "Hide" : "Show"} {g.earlier.length} earlier conversation{g.earlier.length === 1 ? "" : "s"}
                      <ChevronDown className={`h-3 w-3 shrink-0 transition-transform ${showHist ? "rotate-180" : ""}`} />
                    </button>
                  )}
                  {showHist && g.earlier.map((e) => (
                    <ConvRow key={e.id} c={e} active={openId === e.id} onOpen={openRow} nested />
                  ))}
                </div>
              );
            })}
            <ShowMore remaining={remaining} onClick={showMore} label="Load more callers" className="p-3" />
          </div>
        </div>

        {/* Drag-to-resize divider (desktop only) — widen the thread to a comfortable width.
            The width is remembered per browser. Full-screen (the expand icon) stays as an option. */}
        <div data-testid="conv-resize-handle" onPointerDown={startResize}
          role="separator" aria-orientation="vertical" aria-label="Drag to resize conversation panel"
          title="Drag to resize"
          className={`self-stretch items-center justify-center cursor-col-resize group ${expanded ? "hidden" : "hidden lg:flex"}`}>
          <div className="h-14 w-1.5 rounded-full bg-sand-200 group-hover:bg-redline-400 transition-colors" />
        </div>

        {/* Detail panel — on a phone it takes the whole screen (the page header and banners would
            otherwise push the thread below the fold); on desktop it sits beside the list. */}
        <div data-testid="conv-detail-panel" className={`min-w-0 bg-white border-sand-200 overflow-hidden flex flex-col ${
          expanded
            ? "fixed inset-0 z-[70] h-[100dvh] w-screen rounded-none border-0"
            : mobileDetail
            ? "fixed inset-0 z-[60] h-[100dvh] rounded-none lg:static lg:z-auto lg:h-[72vh] lg:rounded-2xl lg:border"
            : "hidden lg:flex h-[72vh] rounded-2xl border"}`}>
          <button data-testid="conv-back-mobile" onClick={() => { setMobileDetail(false); setExpanded(false); }}
            className="lg:hidden flex items-center gap-1.5 text-sm font-semibold text-ink px-4 py-3 border-b border-sand-200 shrink-0 bg-sand-50">
            <ArrowLeft className="h-4 w-4" /> All conversations
          </button>
          <div className="flex-1 min-h-0">
            <ConversationPanel convId={openId} role={role} onChanged={load}
              expanded={expanded} onToggleExpand={() => setExpanded((v) => !v)} />
          </div>
        </div>
      </div>
    </div>
  );
}
